import type { ReactNode } from 'react';
import type { MapStateToPropsFactory } from 'react-redux';
import { connect } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { makeGetBackendReports } from 'state/secondaryMarket/cpOrder/cpOrderForm/cpOrderForm.selectors';
import { secondaryMarketCpOrderFormFieldChanged } from 'state/secondaryMarket/cpOrder/cpOrderForm/input/cpOrderInputForm.actions';
import { SecondaryMarketCpOrderForm } from 'state/secondaryMarket/cpOrder/cpOrderForm/input/cpOrderInputForm.model';
import { secondaryMarketCpOrderPlacementRequestedThunk } from 'state/secondaryMarket/cpOrder/order/order.thunks';
import { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import type { KycDetail, KycScoreStatus } from 'state/secondaryMarket/shared/kyc/kyc.model';
import { fromSecondaryMarket } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';

type OwnProps = Readonly<{
  tabId: string;
  className?: string;
  children: ReactNode;
}>;

type StateProps = Readonly<{
  isOrderPlacementEnabled: boolean;
  comment: string | undefined;
  kycScore: KycScoreStatus | undefined;
  kycDetails: readonly KycDetail[] | undefined;
  backendReports: readonly SecondaryMarketFormError<SecondaryMarketCpOrderForm>[];
}>;

type DispatchProps = Readonly<{
  onPlaceOrder: () => void;
  onCommentChanged: (value: string) => void;
}>;

export type ReadySidePanelProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps: MapStateToPropsFactory<StateProps, OwnProps, AppState> = () => {
  const getCpOrderValidationErrors = fromSecondaryMarket.cpOrder.form.makeGetCpOrderValidationErrors();
  const isReadyToPlaceOrder = fromSecondaryMarket.cpOrder.makeIsReadyToPlaceOrder(getCpOrderValidationErrors);
  const getBackendReports = makeGetBackendReports();
  return (state: AppState, { tabId }: OwnProps): StateProps => {
    const isOrderPlacementEnabled = isReadyToPlaceOrder(state, tabId);
    const comment = fromSecondaryMarket.cpOrder.form.getFormComment(state, tabId);
    const kycScore = fromSecondaryMarket.cpOrder.referential.kyc.getKycStatusScore(state, tabId);
    const kycDetails = fromSecondaryMarket.cpOrder.referential.kyc.getKycStatusDetails(state, tabId);
    return {
      isOrderPlacementEnabled,
      comment,
      kycScore,
      kycDetails,
      backendReports: getBackendReports(state, tabId),
    };
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch, { tabId }: OwnProps): DispatchProps => {
  return {
    onPlaceOrder() {
      dispatch(secondaryMarketCpOrderPlacementRequestedThunk(tabId));
      logAnalytics.trackEvent('Secondary market', 'Request for place order');
    },
    onCommentChanged: value => {
      dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'comment', value));
    },
  };
};

export const connectReadySidePanel = connect(mapStateToProps, mapDispatchToProps);
